import Decimal from 'decimal.js';
import { convertUsdToEur } from './eurFx';
import type { LedgerEvent, LotBasisRepairMeta } from './types';
import { usdcUsdPerUnitOnUtcDayNearest } from './usdcUsdDaily';

/** USD-pegged stables; use bundled USDC/USD daily series as USD value per coin (same family). */
const STABLE_USD_PEG = new Set(['USDC', 'USDT', 'DAI', 'BUSD', 'TUSD']);

const D0 = new Decimal(0);

function syntheticRepairMeta(sourceRef: string | undefined): LotBasisRepairMeta {
  return {
    basisRepaired: true,
    basisRepairMethod: 'synthetic_stable_inventory_backfill',
    basisRepairConfidence: 'medium',
    basisRepairSourceRef: sourceRef,
    basisRepairPass: 2,
    basisValuationSource: 'usdc_usd_daily_csv_plus_frankfurter',
    basisValuationFlag: 'fallback_priced',
  };
}

async function eurForStableShortfall(
  amount: Decimal,
  date: Date,
  signal?: AbortSignal
): Promise<Decimal> {
  if (signal?.aborted) throw new Error('aborted');
  const unitUsd = usdcUsdPerUnitOnUtcDayNearest(date) ?? 1;
  const usdNotional = amount.abs().mul(unitUsd);
  return convertUsdToEur(usdNotional, date);
}

/**
 * When a Crypto.com stablecoin disposal would consume more than tracked inventory, insert a synthetic
 * acquisition immediately before it (timestamp `disposal.ts - 1`) so FIFO has a lot. Basis =
 * bundled `usdc-usd-max.csv` USD/unit × Frankfurter USD→EUR for that UTC day.
 *
 * Only runs for **global** FIFO (no wallet-scoped inventory); wallet continuity path skips this.
 */
export async function injectSyntheticStablecoinAcquisitionsForShortfall(
  events: LedgerEvent[],
  options?: { signal?: AbortSignal }
): Promise<{ events: LedgerEvent[]; inserted: number }> {
  const signal = options?.signal;
  const sorted = [...events].sort((a, b) => a.ts - b.ts);
  const out: LedgerEvent[] = [];
  const balance = new Map<string, Decimal>();
  let inserted = 0;
  let injectCounter = 0;

  for (const ev of sorted) {
    if (signal?.aborted) break;

    if (ev.kind === 'internal_transfer') {
      out.push(ev);
      continue;
    }
    if (ev.classification === 'UNKNOWN_REVIEW' && ev.eurValue == null) {
      out.push(ev);
      continue;
    }
    if (ev.eurValue == null) {
      out.push(ev);
      continue;
    }

    if (ev.kind === 'acquisition' || ev.kind === 'income') {
      const a = ev.asset.toUpperCase();
      balance.set(a, (balance.get(a) ?? D0).plus(ev.amount));
      out.push(ev);
      continue;
    }

    if (ev.kind !== 'disposal') {
      out.push(ev);
      continue;
    }

    const a = ev.asset.toUpperCase();
    const need = ev.amount;

    if (ev.source !== 'crypto_com' || !STABLE_USD_PEG.has(a)) {
      const b = balance.get(a) ?? D0;
      if (b.gte(need)) balance.set(a, b.minus(need));
      else balance.set(a, D0);
      out.push(ev);
      continue;
    }

    let bal = balance.get(a) ?? D0;
    if (bal.gte(need)) {
      balance.set(a, bal.minus(need));
      out.push(ev);
      continue;
    }

    const shortfall = need.minus(bal);
    balance.set(a, D0);

    const date = new Date(ev.ts);
    let eurValue: Decimal;
    try {
      eurValue = await eurForStableShortfall(shortfall, date, signal);
    } catch {
      out.push(ev);
      continue;
    }
    if (signal?.aborted) break;

    injectCounter += 1;
    const synRef = `${ev.ref ?? 'no_ref'}_synthetic_stable_${injectCounter}`;
    const synthetic: LedgerEvent = {
      ts: ev.ts - 1,
      source: 'crypto_com',
      kind: 'acquisition',
      asset: a,
      amount: shortfall,
      eurValue,
      ref: synRef,
      classification: 'TRANSFER_IN',
      fiTaxCategory: 'ACQUISITION',
      acquisitionProvenance: 'recovered',
      acquisitionProvenanceFlag: 'normal',
      lotBasisRepair: syntheticRepairMeta(ev.ref),
      explanation:
        'Synthetic acquisition: stablecoin disposal exceeded tracked inventory (missing earlier acquisitions). Basis = bundled USDC/USD daily × ECB USD→EUR (disposal UTC day).',
      reviewFlags: ['synthetic_stable_inventory_backfill'],
    };
    out.push(synthetic);
    inserted += 1;
    out.push(ev);
  }

  const final = out.sort((a, b) => {
    if (a.ts !== b.ts) return a.ts - b.ts;
    return kindOrder(a) - kindOrder(b);
  });
  return { events: final, inserted };
}

function kindOrder(ev: LedgerEvent): number {
  if (ev.kind === 'acquisition' || ev.kind === 'income') return 0;
  if (ev.kind === 'disposal') return 1;
  return 2;
}
